/**
 * The agent this browser last chose to talk to.
 *
 * Separate from `agent-identity.ts` because that module promises to be pure — every function
 * in it is testable without a DOM — and this one is two lines of `localStorage` and the
 * try/catch they need.
 *
 * **Why remember it at all.** A conversation is pinned to its agent at creation and can never
 * be repointed (AGM-007). Someone whose work is with one non-default agent therefore pays for
 * a forgotten picker with a conversation they have to abandon and start again. The choice is
 * never applied silently: both surfaces that read it name the agent on the control itself
 * before anything is created.
 *
 * **Why not the server.** There is no user-preference endpoint of this shape, and adding a
 * table and a migration to hold a picker default is a poor trade. Per browser is also the
 * honest scope of the promise — this is a convenience, not a setting.
 *
 * Every read is validated against the gallery by `pickAgent`, so a remembered id that has been
 * retired, unpublished or unshared since degrades to the deployment default. Nothing here has
 * to know that; it stores a string.
 */

const KEY = 'ralph.agent'

/**
 * Swallowed rather than reported: Safari in private browsing throws on both `getItem` and
 * `setItem`, and a picker that cannot remember a preference is not a failure anybody needs to
 * be told about. The caller's fallback is the deployment default, which is the same thing a
 * first visit gets.
 */
export const readPreferredAgent = (): string | null => {
  try {
    return localStorage.getItem(KEY)
  } catch {
    return null
  }
}

export const rememberPreferredAgent = (agentId: string): void => {
  try {
    localStorage.setItem(KEY, agentId)
  } catch {
    // See above.
  }
}
